import React, { useState } from 'react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BeakerIcon,
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { DailyFeedMix } from '../api/nutrition';
import { useFormatting } from '../hooks/useFormatting';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface DailyFeedMixViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedMix: DailyFeedMix | null;
  onEdit?: (feedMix: DailyFeedMix) => void;
  onDelete?: (feedMixId: string) => void;
  onDuplicate?: (feedMix: DailyFeedMix) => void;
  onApprove?: (feedMixId: string, approved: boolean) => void;
}

export default function DailyFeedMixViewModal({
  isOpen,
  onClose,
  feedMix,
  onEdit,
  onDelete,
  onDuplicate,
  onApprove
}: DailyFeedMixViewModalProps) {
  const { formatCurrency, formatDate } = useFormatting();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!feedMix) return null;

  const handleDelete = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(feedMix.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to delete feed mix:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApprove = async (approved: boolean) => {
    if (!onApprove) return;
    
    try {
      await onApprove(feedMix.id, approved);
    } catch (error) {
      console.error('Failed to update approval status:', error);
    }
  };

  const totalCost = Number(feedMix.totalCost) || 0;

  const costPerKg = feedMix.totalQuantity > 0 ? totalCost / feedMix.totalQuantity : 0;

  return (
    <>
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title="Daily Feed Mix Details"
        subtitle={formatDate(new Date(feedMix.feedDate))}
        onClose={onClose}
      />
      <BrandModalBody>
                <div className="mb-4 flex items-center space-x-2">
                    {feedMix.metadata?.approved ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Approved
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        Pending
                      </span>
                    )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="space-y-6">
                    <div className="bg-slate-50/80 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-slate-900 mb-3">Basic Information</h4>
                      <dl className="space-y-3">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Date:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {formatDate(new Date(feedMix.feedDate))}
                          </dd>
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Location:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {feedMix.location || 'N/A'}
                          </dd>
                        </div>
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Operator:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {feedMix.operator || 'N/A'}
                          </dd>
                        </div>
                        <div className="flex items-center">
                          <ChartBarIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Total Quantity:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {(typeof feedMix.totalQuantity === 'number' ? feedMix.totalQuantity : parseFloat(feedMix.totalQuantity) || 0).toFixed(1)} kg
                          </dd>
                        </div>
                        <div className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Total Cost:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {formatCurrency(Number(totalCost) || 0)}
                          </dd>
                        </div>
                        <div className="flex items-center">
                          <CurrencyDollarIcon className="h-4 w-4 text-slate-400 mr-2" />
                          <dt className="text-sm text-slate-500 mr-2">Cost per kg:</dt>
                          <dd className="text-sm font-medium text-slate-900">
                            {formatCurrency(Number(costPerKg) || 0)}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Nutritional Summary */}
                    <div className="bg-slate-50/80 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-slate-900 mb-3">Nutritional Summary</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-lg font-semibold text-slate-900">
                            {(Number(feedMix.nutritionalSummary.proteinPercentage) || 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-slate-500">Protein</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-lg font-semibold text-slate-900">
                            {(Number(feedMix.nutritionalSummary.fatPercentage) || 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-slate-500">Fat</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-lg font-semibold text-slate-900">
                            {(Number(feedMix.nutritionalSummary.fiberPercentage) || 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-slate-500">Fiber</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-lg font-semibold text-slate-900">
                            {(Number(feedMix.nutritionalSummary.carbohydratePercentage) || 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-slate-500">Carbohydrate</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-lg font-semibold text-slate-900">
                            {(Number(feedMix.nutritionalSummary.moisturePercentage) || 0).toFixed(1)}%
                          </div>
                          <div className="text-xs text-slate-500">Moisture</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className={`text-lg font-semibold ${
                            (Number(feedMix.nutritionalSummary.qualityScore) || 0) >= 80 ? 'text-green-600' :
                            (Number(feedMix.nutritionalSummary.qualityScore) || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {Number(feedMix.nutritionalSummary.qualityScore) || 0}/100
                          </div>
                          <div className="text-xs text-slate-500">Quality</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Feed Components */}
                  <div className="lg:col-span-2">
                    <div className="bg-slate-50/80 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-slate-900 mb-4">Feed Components</h4>
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-slate-50/80">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Component
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Quantity
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Percentage
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Cost
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Protein
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Fat
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                Carbohydrate
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {feedMix.feedComponents?.map((component, index) => {
                              const quantity = Number(component.quantity) || 0;
                              const totalQty = Number(feedMix.totalQuantity) || 0;
                              const percentage = totalQty > 0 ? (quantity / totalQty) * 100 : 0;
                              const componentCost = 0; // Component cost calculation would need unit price from feed stock
                              
                              return (
                                <tr key={index}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                                    {component.feedStockName || 'Unknown'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {quantity.toFixed(1)} {component.unit || 'kg'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {percentage.toFixed(1)}%
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {formatCurrency(Number(componentCost) || 0)}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {(Number(component.nutritionalContribution?.protein) || 0).toFixed(1)}g/kg
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {(Number(component.nutritionalContribution?.fat) || 0).toFixed(1)}g/kg
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">
                                    {(Number(component.nutritionalContribution?.carbohydrate) || 0).toFixed(1)}g/kg
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Additional Information */}
                    {(feedMix.notes || feedMix.metadata?.specialNotes || feedMix.metadata?.weatherConditions) && (
                      <div className="mt-6 bg-slate-50/80 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-slate-900 mb-3">Additional Information</h4>
                        <div className="space-y-3">
                          {feedMix.notes && (
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Notes:</dt>
                              <dd className="text-sm text-slate-900 mt-1">{feedMix.notes}</dd>
                            </div>
                          )}
                          {feedMix.metadata?.specialNotes && (
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Special Notes:</dt>
                              <dd className="text-sm text-slate-900 mt-1">{feedMix.metadata.specialNotes}</dd>
                            </div>
                          )}
                          {feedMix.metadata?.weatherConditions && (
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Weather Conditions:</dt>
                              <dd className="text-sm text-slate-900 mt-1">{feedMix.metadata.weatherConditions}</dd>
                            </div>
                          )}
                          {feedMix.metadata?.feedingTimes && feedMix.metadata.feedingTimes.length > 0 && (
                            <div>
                              <dt className="text-sm font-medium text-slate-500">Feeding Times:</dt>
                              <dd className="text-sm text-slate-900 mt-1">
                                {feedMix.metadata.feedingTimes.join(', ')}
                              </dd>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

      </BrandModalBody>
      <BrandModalFooter className="flex-wrap">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(feedMix)}
                        className="btn btn-secondary text-sm"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                    )}
                    {onDuplicate && (
                      <button
                        onClick={() => onDuplicate(feedMix)}
                        className="btn btn-secondary text-sm"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        Duplicate
                      </button>
                    )}
                    {onApprove && !feedMix.metadata?.approved && (
                      <button
                        onClick={() => onApprove(feedMix.id, true)}
                        className="btn btn-primary text-sm"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Approve
                      </button>
                    )}
                    {onApprove && feedMix.metadata?.approved && (
                      <button
                        onClick={() => onApprove(feedMix.id, false)}
                        className="btn btn-secondary text-sm"
                      >
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        Unapprove
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="btn btn-danger text-sm"
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    )}
                    <button
                      onClick={onClose}
                      className="btn btn-secondary text-sm"
                    >
                      Close
                    </button>
      </BrandModalFooter>
    </BrandModal>

      <BrandModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidthClass="max-w-lg"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Delete Feed Mix" onClose={() => setShowDeleteConfirm(false)} />
        <BrandModalBody>
          <p className="text-sm text-slate-500">
            Are you sure you want to delete this feed mix from {formatDate(new Date(feedMix.feedDate))}?
            This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="btn btn-danger text-sm disabled:opacity-50"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </BrandModalFooter>
      </BrandModal>
    </>
  );
}
